// THE CLOSE SELECTION (CR-20260916-003, owner screencast 2026-09-16 + twelve rulings in chat).
//
// Shown under the Ignore / Close buttons while Close is selected: which expirations of the saved
// hedge book, and within each, which hedges — with LIVE close prices. Nothing here is priced by
// Maven: every figure is the platform's own saved-hedge-book pricer (`HedgesPricingService`,
// the APG Hedges Total strip's, the Hedge Matrix's, Package Comparison's and the OPG's), built
// the way the OPG builds it and disposed the way the OPG disposes it. The list itself is the OPG
// recipe (options-pricing-grid-base.component.ts, updateHedges): the book for the portfolio
// OWNER, grouped by group id, label from the first leg, side from its type.
//
// THE HEDGE IS THE UNIT (owner ruling 9). A hedge is one group id. A calendar hedge — legs on
// more than one expiration — appears in every expiration box it touches, at its WHOLE close
// price (ruling 1), with a "(cal)" marker (ruling 10), and one tick selects it everywhere: the
// selection is a set of group ids, not a set of (expiration, hedge) pairs.
//
// EXPIRED HEDGES ARE NOT LISTED (ruling 4): a hedge with any leg past 16:20 ET on its expiration
// is dropped before grouping, so it can be neither seen nor selected; the backend refuses a
// selected one anyway, as the backstop.
//
// THE SETUP OWNS THE SELECTION. This component reads it as an @Input and emits a NEW object on
// every change; the Setup assigns it, snapshots it at Apply and diffs it for the yellow cues.
// It also emits a BOOK VIEW — which hedges each expiration holds, the labels, and the per-box
// summary the cradle chips need — because the Setup's validator and chips need what only this
// list knows, and the Setup object holds ids alone.
//
// SESSION ONLY, like everything in Maven: nothing here is persisted.

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {Subscription} from 'rxjs';
import {DateTime} from 'luxon';
import {DetectMethodChanges, isValidNumber, isVoid} from '../../utils';
import {ApgPortfolio} from '../../adjustment-pricing-grid/model/ApgPortfolio';
import {ApgDataService} from '../../adjustment-pricing-grid/services/apg-data.service';
import {HedgePositionsService} from '../../hedging-grid/positions-section/hedge-positions/hedge-positions.service';
import {HedgePosition} from '../../hedging-grid/data-model/hedge-position';
import {HedgesPricingService} from '../../package-comparison/services/hedges-pricing.service';
import {LastQuoteCacheService} from '../../last-quote-cache.service';
import {MessageBusService} from '../../message-bus.service';
import {OptionsChainService} from '../../option-chains.service';
import {ShellTodayService} from '../../shell-clock/shell-today.service';
import {parseOptionTicker} from '../../options-common/options.model';
import {emptyMavenCloseSelection, MavenCloseSelection} from '../maven-domain';
import {MavenCloseBoxSummary} from './maven-setup-breadcrumbs';
import {MavenParamChangeMap} from './maven-setup-param-diff';

/** One hedge of the book, as the list shows it. */
export interface MavenCloseHedge {
    id: string;                 // the saved-book group id — the selection unit
    name: string;               // the hedge's label (the first leg's), or the id when unlabelled
    side: 'calls' | 'puts';
    expirations: string[];      // 'yyyy-MM-dd', distinct, ascending — more than one = calendar
    isCalendar: boolean;
}

/** One expiration of the book: its toggle label, its header, and the hedges it holds. */
export interface MavenCloseExpiration {
    date: string;               // 'yyyy-MM-dd'
    label: string;              // 'dd-MMM-yy (Nd)'
    header: string;             // 'EEE, dd-MMM-yy (Nd)'
    shortLabel: string;         // 'dd-MMM-yy' — the cradle chip's and the error sentence's
    calls: MavenCloseHedge[];
    puts: MavenCloseHedge[];
}

/**
 * What the Setup needs from this list beyond the ids: for the validator, which hedges each
 * expiration holds and how to name the date; for the cradle, the per-box summary.
 */
export interface MavenCloseBookView {
    hedgesByExpiration: { [date: string]: string[] };
    labels: { [date: string]: string };
    boxes: MavenCloseBoxSummary[];
}

@Component({
    selector: 'ets-maven-close-selection',
    templateUrl: './maven-close-selection.component.html',
    styleUrls: ['./maven-close-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenCloseSelectionComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _hedgePositions: HedgePositionsService,
        private readonly _optionChains: OptionsChainService,
        lastQuoteCache: LastQuoteCacheService,
        messageBus: MessageBusService,
        apgDataService: ApgDataService,
        // CR-20260916-005: the shell's "today" for the DTE badges.
        private readonly _shellToday: ShellTodayService
    ) {
        // Built the way the OPG builds it (options-pricing-grid-base.component.ts:168): a plain
        // class over the four root services, one instance per host, disposed by the host.
        this._pricing = new HedgesPricingService(_hedgePositions, lastQuoteCache, messageBus, apgDataService);
    }

    private readonly _pricing: HedgesPricingService;
    private _priceSubscription: Subscription | null = null;
    private _todaySubscription: Subscription | null = null;

    private _portfolio: ApgPortfolio | null = null;
    private _selection: MavenCloseSelection = emptyMavenCloseSelection();
    // Guards a late book read against a portfolio that has since changed.
    private _loadToken = 0;

    /** The book's expirations, ascending, each with its hedges. Empty until loaded. */
    expirations: MavenCloseExpiration[] = [];

    /** Every non-expired hedge by id. */
    private _hedgesById: { [id: string]: MavenCloseHedge } = {};

    loading = false;

    @Input() disabled = false;

    /** The Setup's diff map, for the yellow cues on a changed toggle or tick. */
    @Input() changedKeys: MavenParamChangeMap | null = null;

    @Input()
    set portfolio(value: ApgPortfolio | null) {
        const before = this._portfolio;
        this._portfolio = value || null;
        if (!samePortfolio(before, this._portfolio)) {
            this.reload();
        }
    }

    get portfolio(): ApgPortfolio | null {
        return this._portfolio;
    }

    @Input()
    set selection(value: MavenCloseSelection | null) {
        this._selection = value ? {
            expirations: (value.expirations || []).slice(),
            hedgeIds: (value.hedgeIds || []).slice()
        } : emptyMavenCloseSelection();
    }

    get selection(): MavenCloseSelection {
        return this._selection;
    }

    /** A NEW selection object on every change. The Setup assigns it. */
    @Output() selectionChange = new EventEmitter<MavenCloseSelection>();

    /** The list as built, and the per-box summary, after every load and every change. */
    @Output() bookChange = new EventEmitter<MavenCloseBookView>();

    ngOnInit(): void {
        // Every quote tick re-prices the containers and emits; the figures are pulled from the
        // service by the getters below, so a tick needs nothing but a redraw.
        this._priceSubscription = this._pricing.hedgePriceChanged$.subscribe(() => {
            this._changeDetector.detectChanges();
        });
        // CR-20260916-005: the day flipped — the DTE badges (and the expired filter behind the
        // list) count from a new today. Rebuild from the book.
        this._todaySubscription = this._shellToday.changed$.subscribe(() => {
            if (this._portfolio && !this.loading) {
                this.reload();
            }
        });
        // The portfolio input lands BEFORE ngOnInit and its setter already started the load
        // (which inits the pricer itself); this covers a host that binds the portfolio later.
        if (this._portfolio && !this.loading && this.expirations.length === 0) {
            this.reload();
        }
    }

    ngOnDestroy(): void {
        if (this._priceSubscription) {
            this._priceSubscription.unsubscribe();
            this._priceSubscription = null;
        }
        if (this._todaySubscription) {
            this._todaySubscription.unsubscribe();
            this._todaySubscription = null;
        }
        // Releases the tickers (the dispose leak was fixed in this CR, owner ruling 11).
        this._pricing.dispose();
    }

    // ---- the list ---------------------------------------------------------------------------

    private async reload(): Promise<void> {
        const token = ++this._loadToken;
        const portfolio = this._portfolio;

        this.expirations = [];
        this._hedgesById = {};

        // A new portfolio is a new book: drop the old subscriptions and containers first, so the
        // service does not answer the old key from its cache.
        this._pricing.dispose();
        this._pricing.init();

        if (!portfolio) {
            this.emitBook();
            this._changeDetector.detectChanges();
            return;
        }

        this.loading = true;
        this._changeDetector.detectChanges();

        let positions: HedgePosition[] = [];
        try {
            positions = await this._hedgePositions.getHedgePositions(portfolio);
        } catch (e) {
            console.error(e);
            positions = [];
        }

        if (token !== this._loadToken) {
            return;   // the portfolio changed under the read
        }

        this.build(positions || []);
        this.pruneSelection();
        this.loading = false;

        // The pricer reads the same book itself and subscribes its tickers.
        this._pricing.subscribe(portfolio);

        this.emitBook();
        this._changeDetector.detectChanges();
    }

    // The OPG recipe, with two Maven rules: a hedge with ANY expired leg is dropped whole (ruling
    // 4), and a hedge's expirations are collected across its legs (a calendar spans several).
    private build(positions: HedgePosition[]): void {
        const byId: { [id: string]: HedgePosition[] } = {};
        const order: string[] = [];

        for (let i = 0; i < positions.length; i++) {
            const p = positions[i];
            if (!p || !p.groupId || (p.type !== 'Call' && p.type !== 'Put')) {
                continue;
            }
            if (!byId[p.groupId]) {
                byId[p.groupId] = [];
                order.push(p.groupId);
            }
            byId[p.groupId].push(p);
        }

        const hedges: MavenCloseHedge[] = [];

        for (let i = 0; i < order.length; i++) {
            const legs = byId[order[i]];
            let expired = false;
            const dates: string[] = [];
            for (let j = 0; j < legs.length; j++) {
                if (this._optionChains.isOptionExpired(legs[j].ticker)) {
                    expired = true;
                    break;
                }
                const d = expirationOf(legs[j]);
                if (d && dates.indexOf(d) < 0) {
                    dates.push(d);
                }
            }
            if (expired || dates.length === 0) {
                continue;
            }
            dates.sort();
            hedges.push({
                id: order[i],
                name: legs[0].label || order[i],
                side: legs[0].type === 'Call' ? 'calls' : 'puts',
                expirations: dates,
                isCalendar: dates.length > 1
            });
        }

        const allDates: string[] = [];
        for (let i = 0; i < hedges.length; i++) {
            this._hedgesById[hedges[i].id] = hedges[i];
            for (let j = 0; j < hedges[i].expirations.length; j++) {
                if (allDates.indexOf(hedges[i].expirations[j]) < 0) {
                    allDates.push(hedges[i].expirations[j]);
                }
            }
        }
        allDates.sort();

        // CR-20260916-005: the DTE badge counts from the shell's "today" (the QG replay cursor,
        // or the server time), never the browser clock. Unknown today = no badge, not a wrong one.
        const todayKey = this._shellToday.todayKey(this._portfolio ? this._portfolio.asset : undefined);
        const today = todayKey ? DateTime.fromISO(todayKey) : null;
        this.expirations = allDates.map(date => {
            const dt = DateTime.fromISO(date);
            const dte = today ? Math.round(dt.startOf('day').diff(today.startOf('day'), 'days').days) : null;
            const badge = dte === null ? '' : ' (' + dte + 'd)';
            const shortLabel = dt.toFormat('dd-MMM-yy');
            return {
                date: date,
                shortLabel: shortLabel,
                label: shortLabel + badge,
                header: dt.toFormat('EEE') + ', ' + shortLabel + badge,
                calls: hedges.filter(h => h.side === 'calls' && h.expirations.indexOf(date) >= 0),
                puts: hedges.filter(h => h.side === 'puts' && h.expirations.indexOf(date) >= 0)
            };
        });
    }

    // A selection that names what the list no longer holds (the book changed) is trimmed to what
    // it does; the Setup hears about it like any other change.
    private pruneSelection(): void {
        const dates = this.expirations.map(e => e.date);
        const expirations = this._selection.expirations.filter(d => dates.indexOf(d) >= 0);
        const hedgeIds = this._selection.hedgeIds.filter(id => !!this._hedgesById[id]);
        if (expirations.length !== this._selection.expirations.length
            || hedgeIds.length !== this._selection.hedgeIds.length) {
            this.commit({expirations: expirations, hedgeIds: hedgeIds});
        }
    }

    // ---- the view ---------------------------------------------------------------------------

    get boxes(): MavenCloseExpiration[] {
        return this.expirations.filter(e => this.isExpirationOn(e.date));
    }

    isExpirationOn(date: string): boolean {
        return this._selection.expirations.indexOf(date) >= 0;
    }

    isSelected(id: string): boolean {
        return this._selection.hedgeIds.indexOf(id) >= 0;
    }

    /** The side's (or the whole box's) checkbox: true, false, or undefined = partial. */
    groupValue(box: MavenCloseExpiration, side: 'calls' | 'puts' | 'all'): boolean | undefined {
        const list = this.scope(box, side);
        if (list.length === 0) {
            return false;
        }
        let n = 0;
        for (let i = 0; i < list.length; i++) {
            if (this.isSelected(list[i].id)) {
                n++;
            }
        }
        if (n === 0) {
            return false;
        }
        return n === list.length ? true : undefined;
    }

    hedgePrice(id: string): number | null {
        return this._pricing.getHedgeGroupCostOrNull(id);
    }

    /** The side's sum over the box, or the whole box's. Null until at least one hedge is priced. */
    groupPrice(box: MavenCloseExpiration, side: 'calls' | 'puts' | 'all'): number | null {
        const list = this.scope(box, side);
        let sum = 0;
        let any = false;
        for (let i = 0; i < list.length; i++) {
            const p = this.hedgePrice(list[i].id);
            if (p !== null) {
                sum += p;
                any = true;
            }
        }
        return any ? sum : null;
    }

    moneyText(value: number | null): string {
        if (value === null || value === undefined || !isValidNumber(value)) {
            return '—';
        }
        return (value < 0 ? '-' : '') + '$' + Math.abs(value).toFixed(2);
    }

    signClass(value: number | null): 'credit' | 'debit' | null {
        if (value === null || value === undefined) {
            return null;
        }
        return value > 0 ? 'credit' : (value < 0 ? 'debit' : null);
    }

    expirationsChanged(): boolean {
        return !!(this.changedKeys && this.changedKeys['existingHedges.expirations']);
    }

    // THIS hedge was added or removed since the run (owner vrequest 2026-09-16): the yellow
    // border around its name, in every box that lists it. The set-level key stays for the
    // section header and the cradle chips.
    hedgeChanged(id: string): boolean {
        return !!(this.changedKeys && this.changedKeys['existingHedges.hedge.' + id]);
    }

    trackByDate(_: number, e: MavenCloseExpiration): string {
        return e.date;
    }

    trackById(_: number, h: MavenCloseHedge): string {
        return h.id;
    }

    // ---- the edits --------------------------------------------------------------------------

    @DetectMethodChanges()
    toggleExpiration(date: string): void {
        if (this.disabled) {
            return;
        }
        const on = this.isExpirationOn(date);
        const expirations = on
            ? this._selection.expirations.filter(d => d !== date)
            : this._selection.expirations.concat([date]);
        // Untoggling drops every selected hedge that no longer appears in any open box; a hedge
        // still visible in another box (a calendar) stays selected.
        const visible = this.expirations
            .filter(e => expirations.indexOf(e.date) >= 0)
            .reduce((ids, e) => ids.concat(e.calls.map(h => h.id), e.puts.map(h => h.id)), [] as string[]);
        const hedgeIds = this._selection.hedgeIds.filter(id => visible.indexOf(id) >= 0);
        this.commit({expirations: expirations, hedgeIds: hedgeIds});
    }

    // ⚠ USER EVENTS ONLY. DevExtreme raises onValueChanged for a PROGRAMMATIC value change too
    // — a row tick turns its parent box partial (value undefined), which would re-enter here
    // and read as "select all". `e.event` is set only for a click or a key, the same guard the
    // Setup's portfolio box uses against its own echo. The name span's click passes no event and
    // is the one caller allowed through without it.
    @DetectMethodChanges()
    onHedgeToggle(id: string, value: boolean, e?: any): void {
        if (this.disabled || (e && !e.event) || this.isSelected(id) === !!value) {
            return;
        }
        this.setHedges([id], !!value);
    }

    @DetectMethodChanges()
    onGroupToggle(box: MavenCloseExpiration, side: 'calls' | 'puts' | 'all', e: any): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        // A click on a partial box means "all", never "none": anything but an explicit false
        // selects the scope.
        const on = e.value !== false;
        this.setHedges(this.scope(box, side).map(h => h.id), on);
    }

    private setHedges(ids: string[], on: boolean): void {
        let hedgeIds = this._selection.hedgeIds.slice();
        for (let i = 0; i < ids.length; i++) {
            const at = hedgeIds.indexOf(ids[i]);
            if (on && at < 0) {
                hedgeIds.push(ids[i]);
            } else if (!on && at >= 0) {
                hedgeIds.splice(at, 1);
            }
        }
        this.commit({expirations: this._selection.expirations.slice(), hedgeIds: hedgeIds});
    }

    private scope(box: MavenCloseExpiration, side: 'calls' | 'puts' | 'all'): MavenCloseHedge[] {
        if (side === 'calls') {
            return box.calls;
        }
        if (side === 'puts') {
            return box.puts;
        }
        return box.calls.concat(box.puts);
    }

    private commit(next: MavenCloseSelection): void {
        this._selection = next;
        this.selectionChange.emit({expirations: next.expirations.slice(), hedgeIds: next.hedgeIds.slice()});
        this.emitBook();
    }

    private emitBook(): void {
        const hedgesByExpiration: { [date: string]: string[] } = {};
        const labels: { [date: string]: string } = {};
        for (let i = 0; i < this.expirations.length; i++) {
            const e = this.expirations[i];
            hedgesByExpiration[e.date] = e.calls.map(h => h.id).concat(e.puts.map(h => h.id));
            labels[e.date] = e.shortLabel;
        }
        const boxes: MavenCloseBoxSummary[] = this.boxes.map(b => ({
            date: b.date,
            label: b.shortLabel,
            callNames: b.calls.filter(h => this.isSelected(h.id)).map(h => h.name),
            callTotal: b.calls.length,
            putNames: b.puts.filter(h => this.isSelected(h.id)).map(h => h.name),
            putTotal: b.puts.length
        }));
        this.bookChange.emit({hedgesByExpiration: hedgesByExpiration, labels: labels, boxes: boxes});
    }
}

// A leg's expiration as 'yyyy-MM-dd': the book's own field when it holds one, else parsed off
// the option ticker (the Hedging Grid keeps both; the backend's close order reads the field).
function expirationOf(leg: HedgePosition): string | null {
    if (leg.expiration && /^\d{4}-\d{2}-\d{2}$/.test(leg.expiration)) {
        return leg.expiration;
    }
    const parsed = parseOptionTicker(leg.ticker);
    return parsed && parsed.expiration ? parsed.expiration : null;
}

function samePortfolio(a: ApgPortfolio | null, b: ApgPortfolio | null): boolean {
    if (isVoid(a) && isVoid(b)) {
        return true;
    }
    if (isVoid(a) || isVoid(b)) {
        return false;
    }
    return a.id === b.id && a.userId === b.userId;
}
